import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Cake,
  Phone as PhoneIcon,
  Mail,
  Search,
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import { formatCurrency, formatRelative } from '@/lib/utils'
import type {
  APIResponse,
  CustomerListParams,
  CustomerListResponse,
  CustomerRow,
  LifecycleStage,
} from '@/types'
import { AdminCustomerProfile } from './AdminCustomerProfile'

const STAGE_LABEL: Record<LifecycleStage, string> = {
  new: 'New',
  active: 'Active',
  at_risk: 'At risk',
  lapsed: 'Lapsed',
  vip: 'VIP',
}

const STAGE_BADGE: Record<LifecycleStage, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 border-blue-200',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 border-emerald-200',
  at_risk: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-amber-200',
  lapsed: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200 border-rose-200',
  vip: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200 border-purple-200',
}

const MONTH_LABEL = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/**
 * Customer Directory — Phase 1 of the Bhookly marketing loop.
 *
 * Surfaces every guest the counter has captured details for, with the
 * derived traits (visit_count, lifetime_spend, last_visit_at,
 * lifecycle_stage) computed in the backend. Soft-deleted rows
 * (merged_into IS NOT NULL) are excluded server-side.
 *
 * The search debounces 250ms so a fast typist doesn't fire a query per
 * keystroke. Filters live in query state (page/search/stage/birthday/sort)
 * so deep-links into a specific view work after a reload.
 */
export function AdminCustomersDirectory() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [stage, setStage] = useState<LifecycleStage | 'all'>('all')
  const [birthdayThisMonth, setBirthdayThisMonth] = useState(false)
  const [sort, setSort] = useState<NonNullable<CustomerListParams['sort']>>('last_visit')
  const [page, setPage] = useState(1)
  const [selectedID, setSelectedID] = useState<string | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => window.clearTimeout(t)
  }, [search])

  // Reset to page 1 whenever a filter changes so the user doesn't end up
  // on page 4 of a list whose new filter only has 2 pages of results.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, stage, birthdayThisMonth, sort])

  const params: CustomerListParams = useMemo(() => {
    const p: CustomerListParams = { sort, page, page_size: 50 }
    if (debouncedSearch) p.search = debouncedSearch
    if (stage !== 'all') p.stage = stage
    if (birthdayThisMonth) p.birthday_month = 'current'
    return p
  }, [debouncedSearch, stage, birthdayThisMonth, sort, page])

  const listQuery = useQuery<APIResponse<CustomerListResponse>>({
    queryKey: ['customers', 'list', params],
    queryFn: () => apiClient.listCustomers(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })

  const items = listQuery.data?.data?.items ?? []
  const total = listQuery.data?.data?.total ?? 0
  const pageSize = listQuery.data?.data?.page_size ?? 50
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const currentMonth = new Date().getMonth() + 1

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Customer Directory</h1>
        <p className="mt-1 text-muted-foreground max-w-3xl">
          See every guest at a glance — lifetime spend, visits, and lifecycle
          stage
        </p>
      </div>

      <div className="space-y-4">
        {/* Filter bar */}
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
            <div className="space-y-1.5">
              <label htmlFor="customer-search" className="text-xs font-medium text-muted-foreground">
                Search
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                  id="customer-search"
                  type="search"
                  placeholder="Name, phone, or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-10 pl-9"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="space-y-1.5 min-w-[10rem]">
              <label className="text-xs font-medium text-muted-foreground">Lifecycle</label>
              <Select value={stage} onValueChange={(v) => setStage(v as LifecycleStage | 'all')}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="at_risk">At risk</SelectItem>
                  <SelectItem value="lapsed">Lapsed</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 min-w-[11rem]">
              <label className="text-xs font-medium text-muted-foreground">Sort by</label>
              <Select value={sort} onValueChange={(v) => setSort(v as NonNullable<CustomerListParams['sort']>)}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_visit">Last visit</SelectItem>
                  <SelectItem value="lifetime_spend">Lifetime spend</SelectItem>
                  <SelectItem value="visit_count">Visit count</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <span className="block text-xs font-medium text-muted-foreground">Birthday</span>
              <BirthdayMonthButton
                active={birthdayThisMonth}
                label={birthdayThisMonth ? `In ${MONTH_LABEL[currentMonth]}` : 'This month'}
                onToggle={() => setBirthdayThisMonth((v) => !v)}
              />
            </div>
          </div>

          {/* Results table */}
          <div className="rounded-lg border border-border/80 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="w-[20%]">Name</TableHead>
                  <TableHead className="w-[17%]">Phone</TableHead>
                  <TableHead className="w-[17%] hidden lg:table-cell">Email</TableHead>
                  <TableHead className="w-[16%] hidden xl:table-cell">Last visit</TableHead>
                  <TableHead className="w-[8%] text-right">Visits</TableHead>
                  <TableHead className="w-[13%] text-right">Lifetime</TableHead>
                  <TableHead className="w-[9%]">Stage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center text-sm text-muted-foreground">
                      {debouncedSearch || stage !== 'all' || birthdayThisMonth
                        ? 'No customers match these filters.'
                        : 'No customers yet — they\'ll appear here once guests give their details at checkout.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <CustomerListRow
                      key={row.id}
                      row={row}
                      currentMonth={currentMonth}
                      onClick={() => setSelectedID(row.id)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div>
              {total > 0 ? (
                <>
                  Showing{' '}
                  <span className="font-medium text-foreground">{(page - 1) * pageSize + 1}</span>
                  {'-'}
                  <span className="font-medium text-foreground">
                    {Math.min(page * pageSize, total)}
                  </span>{' '}
                  of <span className="font-medium text-foreground">{total.toLocaleString()}</span>
                </>
              ) : (
                <>No results</>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={page === 1 || listQuery.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 gap-1"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <span className="px-2 tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={page >= totalPages || listQuery.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-8 gap-1"
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>

      <AdminCustomerProfile
        customerID={selectedID}
        open={selectedID !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedID(null)
        }}
      />
    </div>
  )
}

function CustomerListRow({
  row,
  currentMonth,
  onClick,
}: {
  row: CustomerRow
  currentMonth: number
  onClick: () => void
}) {
  const isBirthdayMonth = row.birthday_month === currentMonth
  return (
    <TableRow
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className="cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
    >
      <TableCell className="text-[15px] font-medium">
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate">
            {row.display_name || <span className="text-muted-foreground italic">unnamed</span>}
          </span>
          {row.lifecycle_stage === 'vip' && (
            <Star className="h-3.5 w-3.5 shrink-0 fill-purple-500 text-purple-500" aria-label="VIP" />
          )}
          {isBirthdayMonth && (
            <Cake className="h-3.5 w-3.5 shrink-0 text-pink-500" aria-label="Birthday this month" />
          )}
        </div>
      </TableCell>
      <TableCell className="font-mono text-sm">
        {row.phone_normalized || row.phone || <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="hidden truncate text-xs lg:table-cell">
        {row.email || <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="hidden text-sm xl:table-cell">
        {row.last_visit_at ? formatRelative(row.last_visit_at) : <span className="text-muted-foreground">Never</span>}
      </TableCell>
      <TableCell className="text-right tabular-nums">{row.visit_count}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.lifetime_spend)}</TableCell>
      <TableCell>
        {row.lifecycle_stage ? (
          <Badge
            variant="outline"
            className={`px-1.5 py-0 text-[10px] uppercase tracking-wider ${STAGE_BADGE[row.lifecycle_stage]}`}
          >
            {STAGE_LABEL[row.lifecycle_stage]}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}

const BIRTHDAY_BURST_EMOJIS = ['🎂', '🎉', '🥳', '🎈', '✨', '🎁', '🍰', '💫']

type BirthdayBurst = { id: number; emoji: string; bx: number; by: number; br: string }

function BirthdayMonthButton({
  active,
  label,
  onToggle,
}: {
  active: boolean
  label: string
  onToggle: () => void
}) {
  const [bursts, setBursts] = useState<BirthdayBurst[]>([])

  const spawnBurst = useCallback(() => {
    const next = BIRTHDAY_BURST_EMOJIS.map((emoji, i) => {
      const angle = (i / BIRTHDAY_BURST_EMOJIS.length) * Math.PI * 2 + (Math.random() - 0.5) * 0.4
      const dist = 36 + Math.random() * 28
      return {
        id: Date.now() + i,
        emoji,
        bx: Math.cos(angle) * dist,
        by: Math.sin(angle) * dist - 12,
        br: `${(Math.random() - 0.5) * 80}deg`,
      }
    })
    setBursts((prev) => [...prev, ...next])
    window.setTimeout(() => {
      setBursts((prev) => prev.filter((p) => !next.some((n) => n.id === p.id)))
    }, 800)
  }, [])

  const handleClick = () => {
    if (!active) {
      spawnBurst()
    } else {
      setBursts([])
    }
    onToggle()
  }

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={handleClick}
      className={`bhk-birthday-btn inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${active ? 'bhk-birthday-btn--active' : ''}`}
    >
      {bursts.map((p) => (
        <span
          key={p.id}
          className="bhk-birthday-btn__emoji"
          style={{ '--bx': `${p.bx}px`, '--by': `${p.by}px`, '--br': p.br } as CSSProperties}
          aria-hidden
        >
          {p.emoji}
        </span>
      ))}
      <Cake className={`relative z-10 h-4 w-4 ${active ? 'drop-shadow-sm' : 'text-pink-500'}`} />
      <span className="relative z-10">{label}</span>
    </button>
  )
}

/** Re-export helpers for the profile drawer. */
export { STAGE_LABEL, STAGE_BADGE, MONTH_LABEL }
export const _phoneIcon = PhoneIcon
export const _mailIcon = Mail
